import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, IconButton, Chip,
  Tooltip, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel,
  Select, MenuItem, Switch, FormHelperText,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Coins, Plus, Pencil, RefreshCw, Clock, Percent, Calculator } from "lucide-react";
import { useColorMode } from "../../../theme/ColorModeContext";
import axiosInstance from "../../../services/axiosInstance";
import { useTranslation } from "react-i18next";

const BRAND = "#7A3FE4";
const SCOPE_TONE = {
  global: { bg: "rgba(59,125,240,0.12)", fg: "#2563EB" },
  plan:   { bg: "rgba(232,147,12,0.14)", fg: "#B45309" },
  user:   { bg: "rgba(122,63,228,0.12)", fg: "#6D28D9" },
};
const EMPTY = { scope: "global", planId: "", userId: "", event: "chat_reply", tokens: "", passThrough: "none", passThroughMultiplier: "1.2", effectiveFrom: "", notes: "", costProfile: "" };
const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return String(d); } };

// Tenant Price Card: tokens per EVENT (global / plan / tenant rows, effective-dated,
// never edited in place) + telephony margin per route pattern + a route quote tool.
function PriceCardAdmin() {
  const { t }    = useTranslation("admin");
  const { mode } = useColorMode();
  const isLight  = mode === "light";

  const [rows, setRows]       = useState([]);
  const [options, setOptions] = useState({ events: [], passThrough: [], scopes: [] });
  const [config, setConfig]   = useState(null);
  const [targets, setTargets] = useState({ plans: [], users: [] });
  const [margins, setMargins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [toast, setToast]     = useState(null);
  const [eventFilter, setEventFilter] = useState("");

  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [saving, setSaving]   = useState(false);
  const [dlgError, setDlgError] = useState("");

  const [mForm, setMForm]     = useState({ direction: "outbound", country: "*", numberType: "mobile", margin: "", notes: "" });
  const [mSaving, setMSaving] = useState(false);

  const [quote, setQuote]     = useState({ carrier: "telnyx", direction: "inbound", country: "", numberType: "landline", userId: "" });
  const [quoteRes, setQuoteRes] = useState(null);

  const showToast = (msg, sev = "success") => { setToast({ msg, sev }); setTimeout(() => setToast(null), 6000); };

  const load = useCallback(() => {
    setLoading(true); setError("");
    const params = new URLSearchParams(eventFilter ? { event: eventFilter } : {});
    Promise.all([axiosInstance.get(`/ai/admin/price-card?${params}`), axiosInstance.get("/ai/admin/telephony-margins")])
      .then(([p, m]) => { setRows(p.data.rows || []); setOptions(p.data.options || options); setConfig(p.data.config || null); setMargins(m.data.rows || []); })
      .catch(e => setError(e.response?.data?.error || t("pcLoadFailed")))
      .finally(() => setLoading(false));
  }, [eventFilter, t]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axiosInstance.get("/ai/admin/routing/targets").then(r => setTargets({ plans: r.data.plans || [], users: r.data.users || [] })).catch(() => {}); }, []);

  const openAdd  = () => { setEditing(null); setForm(EMPTY); setDlgError(""); setDlgOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ scope: r.scope, planId: r.planId || "", userId: r.userId != null ? String(r.userId) : "", event: r.event, tokens: String(r.tokens), passThrough: r.passThrough,
      passThroughMultiplier: String(r.passThroughMultiplier), effectiveFrom: "", notes: r.notes || "", costProfile: r.costProfile ? JSON.stringify(r.costProfile) : "" });
    setDlgError(""); setDlgOpen(true);
  };
  const save = async () => {
    setSaving(true); setDlgError("");
    try {
      const body = { ...form, planId: form.scope === "plan" ? form.planId : undefined, userId: form.scope === "user" ? Number(form.userId) : undefined,
        tokens: Number(form.tokens), passThroughMultiplier: Number(form.passThroughMultiplier), effectiveFrom: form.effectiveFrom || undefined, costProfile: form.costProfile || undefined };
      if (editing) await axiosInstance.put(`/ai/admin/price-card/${editing.id}`, body);
      else         await axiosInstance.post("/ai/admin/price-card", body);
      setDlgOpen(false); load(); showToast(editing ? t("pcEdited") : t("pcAdded"));
    } catch (e) { setDlgError(e.response?.data?.error || t("pcSaveFailed")); }
    finally { setSaving(false); }
  };
  const toggle = async (r) => {
    try { await axiosInstance.patch(`/ai/admin/price-card/${r.id}`, { active: !r.active }); setRows(rs => rs.map(x => x.id === r.id ? { ...x, active: !r.active ? 1 : 0 } : x)); }
    catch (e) { showToast(e.response?.data?.error || t("pcSaveFailed"), "error"); }
  };
  const addMargin = async () => {
    setMSaving(true);
    try {
      await axiosInstance.post("/ai/admin/telephony-margins", { routePattern: `${mForm.direction}/${mForm.country || "*"}/${mForm.numberType || "*"}`, margin: Number(mForm.margin), notes: mForm.notes });
      setMForm({ direction: "outbound", country: "*", numberType: "mobile", margin: "", notes: "" }); load(); showToast(t("tmAdded"));
    } catch (e) { showToast(e.response?.data?.error || t("tmSaveFailed"), "error"); }
    finally { setMSaving(false); }
  };
  const toggleMargin = async (m) => {
    try { await axiosInstance.patch(`/ai/admin/telephony-margins/${m.id}`, { active: !m.active }); load(); }
    catch (e) { showToast(e.response?.data?.error || t("tmSaveFailed"), "error"); }
  };
  const runQuote = async () => {
    setQuoteRes(null);
    try { const { data } = await axiosInstance.get(`/ai/admin/telephony-margins/quote?${new URLSearchParams({ ...quote, seconds: 60 })}`); setQuoteRes(data); }
    catch (e) { setQuoteRes({ error: e.response?.data?.error || t("tmQuoteFailed") }); }
  };

  const targetLabel = (r) => r.scope === "global" ? t("rtEveryone") : r.scope === "plan" ? (r.planName || r.planId) : (r.userEmail || r.userName || `#${r.userId}`);
  const headSx = { fontSize: 12, fontWeight: 700, color: "text.secondary", whiteSpace: "nowrap" };
  const cellSx = { fontSize: 13, fontVariantNumeric: "tabular-nums" };
  const card = { p: 2, border: "1px solid", borderColor: "divider", borderRadius: "12px", bgcolor: isLight ? "#fff" : "background.paper" };
  const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };
  const isTel = form.event === "telephony_min";

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: alpha(BRAND, 0.12), display: "grid", placeItems: "center" }}><Coins size={20} color={BRAND} /></Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{t("pcTitle")}</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{t("pcSubtitle", { usd: config?.tokenUsd ?? 0.001 })}</Typography>
        </Box>
        <Button variant="contained" size="small" onClick={openAdd} startIcon={<Plus size={14} />} sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, boxShadow: "none" }}>{t("pcAdd")}</Button>
      </Box>

      {toast && <Alert severity={toast.sev} sx={{ mb: 2, borderRadius: "10px" }}>{toast.msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{error}</Alert>}

      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{t("pcEvent")}</InputLabel>
          <Select label={t("pcEvent")} value={eventFilter} onChange={e => setEventFilter(e.target.value)} sx={{ borderRadius: "10px" }}>
            <MenuItem value="">{t("rtAll")}</MenuItem>
            {(options.events || []).map(ev => <MenuItem key={ev} value={ev} sx={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>{ev}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title={t("refresh")}><IconButton size="small" onClick={load}><RefreshCw size={15} /></IconButton></Tooltip>
      </Box>

      <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: "12px", bgcolor: isLight ? "#fff" : "background.paper", mb: 3 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={headSx}>{t("pcEvent")}</TableCell><TableCell sx={headSx}>{t("rtScope")}</TableCell><TableCell sx={headSx}>{t("rtTarget")}</TableCell>
            <TableCell sx={headSx} align="right">{t("pcTokens")}</TableCell><TableCell sx={headSx}>{t("pcPassThrough")}</TableCell><TableCell sx={headSx} align="right">{t("pcMultiplier")}</TableCell>
            <TableCell sx={headSx}>{t("rtEffective")}</TableCell><TableCell sx={headSx} align="center">{t("rtActive")}</TableCell><TableCell sx={headSx} align="right">{t("rtActions")}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5 }}><CircularProgress size={22} /></TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary", fontSize: 13 }}>{t("pcEmpty")}</TableCell></TableRow>}
            {!loading && rows.map(r => {
              const tone = SCOPE_TONE[r.scope] || SCOPE_TONE.global;
              return (
                <TableRow key={r.id} hover sx={!r.active ? { opacity: 0.5 } : r.isFuture ? { opacity: 0.8 } : undefined}>
                  <TableCell sx={{ ...cellSx, fontFamily: "var(--font-mono)", fontSize: 12 }}>{r.event}</TableCell>
                  <TableCell><Chip size="small" label={t(`rtScope_${r.scope}`)} sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: tone.bg, color: tone.fg }} /></TableCell>
                  <TableCell sx={{ ...cellSx, fontSize: 12 }}>{targetLabel(r)}</TableCell>
                  <TableCell sx={cellSx} align="right">{r.event === "telephony_min" ? <em style={{ opacity: 0.6 }}>{t("pcDerived")}</em> : r.passThrough === "platform_cost" ? <em style={{ opacity: 0.6 }}>{t("pcPlatformCost", { mult: Number(r.passThroughMultiplier).toFixed(2) })}</em> : Number(r.tokens).toLocaleString()}</TableCell>
                  <TableCell sx={{ ...cellSx, fontSize: 12 }}>{r.passThrough === "none" ? "—" : t(`pcPt_${r.passThrough}`)}</TableCell>
                  <TableCell sx={cellSx} align="right">{r.passThrough === "none" ? "—" : `×${Number(r.passThroughMultiplier).toFixed(2)}`}</TableCell>
                  <TableCell sx={{ ...cellSx, fontSize: 12, whiteSpace: "nowrap" }}>
                    {fmtDate(r.effectiveFrom)}
                    {r.isFuture ? <Chip size="small" icon={<Clock size={10} />} label={t("rcScheduled")} sx={{ ml: 0.75, height: 18, fontSize: 10, bgcolor: alpha("#E8930C", 0.12), color: "#B45309", "& .MuiChip-icon": { color: "inherit" } }} /> : null}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    <Switch size="small" checked={!!r.active} onChange={() => toggle(r)} />
                    {r.supersededById ? <Chip size="small" label={`${t("rtReplacedBy")} #${r.supersededById}`} sx={{ ml: 0.5, height: 18, fontSize: 10 }} /> : null}
                  </TableCell>
                  <TableCell align="right"><Tooltip title={t("rtEdit")}><IconButton size="small" onClick={() => openEdit(r)}><Pencil size={14} /></IconButton></Tooltip></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Telephony margins */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" }, gap: 2.5 }}>
        <Box sx={card}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}><Percent size={14} /> {t("tmTitle")}</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>{t("tmDesc", { def: config?.marginDefault ?? 0.15, min: config?.marginMin ?? 0.1, max: config?.marginMax ?? 0.2, floor: config?.platformMinUsd ?? 0.001 })}</Typography>
          <TableContainer sx={{ mb: 1.5 }}><Table size="small">
            <TableHead><TableRow><TableCell sx={headSx}>{t("tmPattern")}</TableCell><TableCell sx={headSx} align="right">{t("tmMargin")}</TableCell><TableCell sx={headSx}>{t("rtEffective")}</TableCell><TableCell sx={headSx} align="center">{t("rtActive")}</TableCell></TableRow></TableHead>
            <TableBody>
              {margins.length === 0 && <TableRow><TableCell colSpan={4} sx={{ color: "text.secondary", fontSize: 12 }}>{t("tmEmpty")}</TableCell></TableRow>}
              {margins.map(m => (
                <TableRow key={m.id} sx={!m.active ? { opacity: 0.5 } : undefined}>
                  <TableCell sx={{ ...cellSx, fontFamily: "var(--font-mono)", fontSize: 12 }}>{m.routePattern}</TableCell>
                  <TableCell sx={cellSx} align="right">{(m.margin * 100).toFixed(0)}%</TableCell>
                  <TableCell sx={{ ...cellSx, fontSize: 12 }}>{fmtDate(m.effectiveFrom)}</TableCell>
                  <TableCell align="center"><Switch size="small" checked={!!m.active} onChange={() => toggleMargin(m)} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></TableContainer>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 1, alignItems: "start" }}>
            <FormControl size="small"><InputLabel>{t("tmDirection")}</InputLabel>
              <Select label={t("tmDirection")} value={mForm.direction} onChange={e => setMForm(f => ({ ...f, direction: e.target.value }))}>
                <MenuItem value="inbound">inbound</MenuItem><MenuItem value="outbound">outbound</MenuItem>
              </Select></FormControl>
            <TextField size="small" label={t("tmCountry")} value={mForm.country} onChange={e => setMForm(f => ({ ...f, country: e.target.value.toUpperCase() }))} sx={inputSx} helperText="ISO-2 or *" />
            <FormControl size="small"><InputLabel>{t("tmType")}</InputLabel>
              <Select label={t("tmType")} value={mForm.numberType} onChange={e => setMForm(f => ({ ...f, numberType: e.target.value }))}>
                {["*", "landline", "mobile", "tollfree"].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select></FormControl>
            <TextField size="small" type="number" label={t("tmMargin")} value={mForm.margin} onChange={e => setMForm(f => ({ ...f, margin: e.target.value }))} inputProps={{ step: 0.01, min: 0, max: 1 }} sx={inputSx} helperText={`${config?.marginMin ?? 0.1}–${config?.marginMax ?? 0.2}`} />
            <Button variant="outlined" size="small" onClick={addMargin} disabled={mSaving || mForm.margin === ""} sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, height: 40 }}>{mSaving ? <CircularProgress size={13} /> : t("tmAdd")}</Button>
          </Box>
        </Box>

        {/* Quote tool */}
        <Box sx={card}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}><Calculator size={14} /> {t("tmQuoteTitle")}</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>{t("tmQuoteDesc")}</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <TextField size="small" label={t("rcProvider")} value={quote.carrier} onChange={e => setQuote(q => ({ ...q, carrier: e.target.value }))} sx={inputSx} />
            <FormControl size="small"><InputLabel>{t("tmDirection")}</InputLabel>
              <Select label={t("tmDirection")} value={quote.direction} onChange={e => setQuote(q => ({ ...q, direction: e.target.value }))}><MenuItem value="inbound">inbound</MenuItem><MenuItem value="outbound">outbound</MenuItem></Select></FormControl>
            <TextField size="small" label={t("tmCountry")} value={quote.country} onChange={e => setQuote(q => ({ ...q, country: e.target.value.toUpperCase() }))} sx={inputSx} />
            <FormControl size="small"><InputLabel>{t("tmType")}</InputLabel>
              <Select label={t("tmType")} value={quote.numberType} onChange={e => setQuote(q => ({ ...q, numberType: e.target.value }))}>{["landline", "mobile", "tollfree"].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}</Select></FormControl>
            <FormControl size="small" sx={{ gridColumn: "1 / -1" }}><InputLabel>{t("rtTenant")}</InputLabel>
              <Select label={t("rtTenant")} value={quote.userId} onChange={e => setQuote(q => ({ ...q, userId: e.target.value }))} MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
                <MenuItem value="">{t("rtNoTenant")}</MenuItem>
                {targets.users.map(u => <MenuItem key={u.id} value={String(u.id)}>#{u.id} · {u.email || u.name}{u.plan ? ` · ${u.plan}` : ""}</MenuItem>)}
              </Select></FormControl>
            <Button variant="outlined" size="small" onClick={runQuote} disabled={!quote.country} sx={{ gridColumn: "1 / -1", textTransform: "none", borderRadius: "10px", fontWeight: 600 }}>{t("tmQuote")}</Button>
          </Box>
          {quoteRes && (
            <Box sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: "10px", bgcolor: alpha(BRAND, 0.06), fontFamily: "var(--font-mono)", fontSize: 12.5 }}>
              {quoteRes.error ? <span style={{ color: "#DC2626" }}>{quoteRes.error}</span> : (
                <span>
                  {quoteRes.routeKey} · cost ${Number(quoteRes.carrierCostUsdPerMin).toFixed(5)}/min{quoteRes.rateMissing ? " (no rate row)" : ""} · m={quoteRes.margin} ({quoteRes.marginSource})
                  <br />max(cost×(1+m), {quoteRes.platformMinUsd}) = <b>${Number(quoteRes.perMinUsd).toFixed(5)}/min</b> ÷ ${quoteRes.tokenPriceUsd} = <b>{Number(quoteRes.tokens).toFixed(2)} tokens/min</b>
                </span>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Add / edit dialog */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>{editing ? `${t("pcEditTitle")} · #${editing.id}` : t("pcAddTitle")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 2 }}>{editing ? t("rtEditDesc") : t("pcAddDesc")}</Typography>
          {dlgError && <Alert severity="error" sx={{ mb: 2 }}>{dlgError}</Alert>}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <FormControl size="small"><InputLabel>{t("rtScope")}</InputLabel>
              <Select label={t("rtScope")} value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}>
                {["global", "plan", "user"].map(s => <MenuItem key={s} value={s}>{t(`rtScope_${s}`)}</MenuItem>)}
              </Select></FormControl>
            {form.scope === "plan" && (
              <FormControl size="small"><InputLabel>{t("rtPlan")}</InputLabel>
                <Select label={t("rtPlan")} value={form.planId} onChange={e => setForm(f => ({ ...f, planId: e.target.value }))}>
                  {targets.plans.map(p => <MenuItem key={p.id} value={p.id}>{p.name} ({p.id})</MenuItem>)}
                </Select></FormControl>
            )}
            {form.scope === "user" && (
              <FormControl size="small"><InputLabel>{t("rtTenant")}</InputLabel>
                <Select label={t("rtTenant")} value={form.userId} onChange={e => setForm(f => ({ ...f, userId: e.target.value }))} MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
                  {targets.users.map(u => <MenuItem key={u.id} value={String(u.id)}>#{u.id} · {u.email || u.name}</MenuItem>)}
                </Select></FormControl>
            )}
            {form.scope === "global" && <Box />}
            <FormControl size="small"><InputLabel>{t("pcEvent")}</InputLabel>
              <Select label={t("pcEvent")} value={form.event} onChange={e => setForm(f => ({ ...f, event: e.target.value }))}>
                {(options.events || []).map(ev => <MenuItem key={ev} value={ev} sx={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>{ev}</MenuItem>)}
              </Select></FormControl>
            <TextField size="small" type="number" label={t("pcTokens")} value={form.tokens} disabled={isTel} onChange={e => setForm(f => ({ ...f, tokens: e.target.value }))} sx={inputSx}
              helperText={isTel ? t("pcTelHelp") : t("pcTokensHelp", { usd: config?.tokenUsd ?? 0.001 })} />
            <FormControl size="small"><InputLabel>{t("pcPassThrough")}</InputLabel>
              <Select label={t("pcPassThrough")} value={form.passThrough} onChange={e => setForm(f => ({ ...f, passThrough: e.target.value }))}>
                {(options.passThrough || []).map(v => <MenuItem key={v} value={v}>{v === "none" ? "—" : t(`pcPt_${v}`)}</MenuItem>)}
              </Select>
              <FormHelperText>{t("pcPassThroughHelp")}</FormHelperText>
            </FormControl>
            <TextField size="small" type="number" label={t("pcMultiplier")} value={form.passThroughMultiplier} disabled={form.passThrough === "none" && !isTel}
              onChange={e => setForm(f => ({ ...f, passThroughMultiplier: e.target.value }))} inputProps={{ step: 0.01, min: 1, max: 5 }} sx={inputSx}
              helperText={isTel && form.scope === "user" ? t("pcTelMultHelp") : t("pcMultiplierHelp")} />
            <TextField size="small" label={t("rcEffectiveFrom")} type="datetime-local" InputLabelProps={{ shrink: true }} value={form.effectiveFrom} onChange={e => setForm(f => ({ ...f, effectiveFrom: e.target.value }))} helperText={t("rcEffectiveHelp")} sx={inputSx} />
            <TextField size="small" label={t("rcNotes")} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} sx={inputSx} inputProps={{ maxLength: 255 }} />
            {!isTel && (
              <TextField size="small" label={t("pcCostProfile")} value={form.costProfile} onChange={e => setForm(f => ({ ...f, costProfile: e.target.value }))} multiline minRows={2}
                sx={{ ...inputSx, gridColumn: { sm: "1 / -1" }, "& textarea": { fontFamily: "var(--font-mono)", fontSize: 12 } }} helperText={t("pcCostProfileHelp")} />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDlgOpen(false)} sx={{ textTransform: "none" }}>{t("cancel")}</Button>
          <Button variant="contained" onClick={save} disabled={saving || (!isTel && form.tokens === "") || (form.scope === "plan" && !form.planId) || (form.scope === "user" && !form.userId)}
            sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none", borderRadius: "10px" }}>
            {saving ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : t("rtSave")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PriceCardAdmin;
